import React, { useEffect, useState, FC } from 'react'
import { motion } from 'framer-motion'

import { observer } from 'mobx-react'

import { Helmet } from 'react-helmet'
import Loading from 'react-loading'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'

import ScrollToTop from '../../../../routing/ScrollToTop'
import { pageVariantsAnimation } from '../../../../utils/constants'
import UserMobx from '../../../../mobx/user'
import BodyBrand from './components/BodyBrand'
import Guests from './components/Guests'
import AddBrandModal from './components/AddBrandModal'
import PublicProgram from '../../../brands/components/PublicProgram'

type TabId = 'identity' | 'products' | 'guests' | 'public'

// Tailwind v2.2 ne génère pas les dégradés à valeurs arbitraires (from-[#hex]).
// On passe donc les éléments colorés/dégradés en styles inline (rendu garanti).
const VOGZ_GRADIENT = 'linear-gradient(45deg, #615AF6, #2A42A7)'

const Brand: FC = () => {
  const user = UserMobx.me.data!
  const isAgency = user?._company.type === 'agency'
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()

  const [activeTab, setActiveTab] = useState<TabId>('identity')
  const [showAddBrand, setShowAddBrand] = useState(false)

  useEffect(() => {
    const brandIdParam = searchParams.get('brand')
    const openNew = searchParams.get('new')
    UserMobx.getBrands((brands) => {
      const newBrandSession = JSON.parse(sessionStorage.getItem('newBrand') ?? '{}')
      if (Object.keys(newBrandSession).length > 0) {
        UserMobx.setBrandSession(newBrandSession)
        sessionStorage.removeItem('newBrand')
      } else if (brandIdParam) {
        const found = (brands ?? []).find((b) => b._id === brandIdParam)
        if (found) UserMobx.setBrandSession(found)
        else UserMobx.resetBrandSession()
      } else if (openNew) {
        setShowAddBrand(true)
      } else if ((brands ?? []).length === 0) {
        setShowAddBrand(true)
      } else {
        UserMobx.resetBrandSession()
      }
    })
  }, [])

  const selected = isAgency ? UserMobx.getBrandSession() : user?.brand
  const brandId = selected?._id ?? ''

  const tabs: { id: TabId; label: string }[] = [
    { id: 'identity', label: t('myprofil:brand-page.tabs.identity') },
    { id: 'products', label: t('myprofil:brand-page.tabs.products') },
    { id: 'guests', label: t('myprofil:brand-page.tabs.guests') },
    { id: 'public', label: t('myprofil:brand-page.tabs.public') },
  ]

  const renderHub = () => (
    <div className="w-full max-w-5xl mx-auto pt-6 px-3">
      {isAgency && (
        <button
          type="button"
          onClick={() => {
            UserMobx.resetBrandSession()
            setActiveTab('identity')
          }}
          className="flex items-center gap-1.5 mb-5"
          style={{ color: '#615AF6', fontWeight: 700, fontFamily: 'Hurme-Bold, sans-serif', fontSize: 14 }}
        >
          <i className="fa-regular fa-angle-left" /> {t('myprofil:brand-page.change-brand')}
        </button>
      )}

      <div
        className="flex gap-1 mb-8 overflow-x-auto"
        style={{ padding: 6, background: '#EEF0F9', borderRadius: 16 }}
      >
        {tabs.map((tab) => {
          const active = activeTab === tab.id
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className="flex-1 whitespace-nowrap"
              style={{
                minWidth: 'fit-content',
                borderRadius: 11,
                padding: '11px 16px',
                fontSize: 14,
                fontWeight: 700,
                fontFamily: 'Hurme-Bold, sans-serif',
                transition: 'all .2s ease',
                cursor: 'pointer',
                background: active ? VOGZ_GRADIENT : 'transparent',
                color: active ? '#fff' : '#8A91B4',
                boxShadow: active ? '0 6px 16px rgba(97,90,246,.35)' : 'none',
              }}
            >
              {tab.label}
            </button>
          )
        })}
      </div>

      <div
        style={{
          background: '#fff',
          borderRadius: 18,
          border: '1px solid #EEF0F7',
          boxShadow: '0 4px 24px rgba(42,66,167,.06)',
          padding: '2rem',
        }}
      >
        {activeTab === 'identity' && <BodyBrand isAgency={isAgency} section="identity" />}
        {activeTab === 'products' && <BodyBrand isAgency={isAgency} section="products" />}
        {activeTab === 'guests' && brandId && <Guests brandId={brandId} />}
        {activeTab === 'public' && brandId && (
          <PublicProgram brandId={brandId} initial={(selected as any)?.public_program} />
        )}
      </div>
    </div>
  )

  const renderBrandGrid = () => (
    <div className="w-full max-w-5xl mx-auto pt-6 px-4">
      <h2 style={{ color: '#2A42A7', fontFamily: 'Hurme-Black, sans-serif', fontSize: 26, marginBottom: 4 }}>
        {t('myprofil:brand-page.select-brand')}
      </h2>
      <p style={{ color: '#9aa0bd', fontSize: 14, marginBottom: 28 }}>
        {t('myprofil:brand-page.desc')}
      </p>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(168px, 1fr))',
          gap: 18,
        }}
      >
        {(UserMobx.allBrands.data ?? []).map((brnd) => (
          <button
            key={brnd._id}
            type="button"
            onClick={() => UserMobx.setBrandSession(brnd as any)}
            className="flex flex-col items-center justify-center"
            style={{
              padding: '28px 18px',
              minHeight: 180,
              gap: 14,
              borderRadius: 18,
              border: '1px solid #EEF0F7',
              background: '#fff',
              boxShadow: '0 6px 24px rgba(42,66,167,.07)',
              cursor: 'pointer',
              transition: 'transform .15s ease, box-shadow .15s ease',
              textAlign: 'center',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)'
              e.currentTarget.style.boxShadow = '0 12px 32px rgba(97,90,246,.18)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)'
              e.currentTarget.style.boxShadow = '0 6px 24px rgba(42,66,167,.07)'
            }}
          >
            {brnd.logo ? (
              <img
                src={brnd.logo}
                alt={brnd.name}
                style={{ width: 72, height: 72, objectFit: 'contain', borderRadius: 16, flexShrink: 0 }}
              />
            ) : (
              <div
                style={{
                  width: 72,
                  height: 72,
                  borderRadius: 16,
                  background: '#E8ECFC',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#615AF6',
                  fontSize: 28,
                  fontWeight: 800,
                  flexShrink: 0,
                }}
              >
                {(brnd.name || '?').charAt(0).toUpperCase()}
              </div>
            )}
            <span
              style={{
                color: '#2A42A7',
                fontFamily: 'Hurme-Bold, sans-serif',
                fontSize: 15,
                lineHeight: 1.25,
              }}
            >
              {brnd.name}
            </span>
          </button>
        ))}
        <button
          type="button"
          onClick={() => setShowAddBrand(true)}
          className="flex flex-col items-center justify-center"
          style={{
            padding: '28px 18px',
            minHeight: 180,
            gap: 12,
            borderRadius: 18,
            border: '2px dashed #D7DBEC',
            background: 'transparent',
            cursor: 'pointer',
            transition: 'all .15s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#615AF6'
            e.currentTarget.style.background = '#F6F7FE'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#D7DBEC'
            e.currentTarget.style.background = 'transparent'
          }}
        >
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              background: '#E8ECFC',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <i className="fa-solid fa-plus" style={{ fontSize: 20, color: '#615AF6' }} />
          </div>
          <span style={{ fontFamily: 'Hurme-Bold, sans-serif', fontSize: 14, color: '#615AF6', textAlign: 'center' }}>
            {t('myprofil:brand-page.add-brand')}
          </span>
        </button>
      </div>
    </div>
  )

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Vogz - Profil - Brand</title>
      </Helmet>
      <main className="flex w-full h-auto items-center justify-start 2xl:ml-0 sm:ml-0 ml-4 flex-col  sm:min-h-full p-4">
        <motion.div
          className="w-full"
          initial="initial"
          animate="in"
          exit="out"
          transition={{ type: 'spring', bounce: 0 }}
          variants={pageVariantsAnimation}
        >
          <ScrollToTop />
          {!UserMobx.me.loading && !UserMobx.me.error ? (
            isAgency ? (
              !UserMobx.allBrands.loading && !UserMobx.allBrands.error ? (
                !selected?._id ? (
                  renderBrandGrid()
                ) : (
                  renderHub()
                )
              ) : (
                <Loading />
              )
            ) : (
              renderHub()
            )
          ) : (
            <Loading />
          )}
        </motion.div>
      </main>
      {showAddBrand && <AddBrandModal onClose={() => setShowAddBrand(false)} />}
    </>
  )
}

export default observer(Brand)
